import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, Loader2, Check, Gift, Utensils, Award, Calendar } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const donationOptions = [
  {
    id: 'school_supplies',
    name: 'School Supplies',
    amount: 25,
    description: 'Provide school supplies for one child',
    icon: Gift,
    color: 'bg-[#38BDF8]'
  },
  {
    id: 'meal_program',
    name: 'Meal Program',
    amount: 50,
    description: 'Feed 10 children for one event',
    icon: Utensils,
    color: 'bg-[#22C55E]'
  },
  {
    id: 'talent_showcase',
    name: 'Talent Showcase',
    amount: 100,
    description: 'Sponsor a talent showcase competition',
    icon: Award,
    color: 'bg-[#FF7A59]'
  },
  {
    id: 'full_event',
    name: 'Full Event Sponsor',
    amount: 500,
    description: 'Sponsor an entire Fun in the Sun event',
    icon: Calendar,
    color: 'bg-[#FACC15]'
  }
];

const DonatePage = () => {
  const navigate = useNavigate();
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [customAmount, setCustomAmount] = useState('');
  const [isCustom, setIsCustom] = useState(false);
  const [donorName, setDonorName] = useState('');
  const [donorEmail, setDonorEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Check if returning from Stripe
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    if (sessionId) {
      navigate(`/donate/success?session_id=${sessionId}`);
    }
  }, [navigate]);

  const handleSelectPackage = (packageId) => {
    setSelectedPackage(packageId);
    setIsCustom(false);
    setCustomAmount('');
  };

  const handleCustomSelect = () => {
    setSelectedPackage(null);
    setIsCustom(true);
  };

  const handleDonate = async () => {
    if (!selectedPackage && !isCustom) {
      toast.error('Please select a donation amount');
      return;
    }

    if (isCustom && (!customAmount || parseFloat(customAmount) <= 0)) {
      toast.error('Please enter a valid donation amount');
      return;
    }

    setIsLoading(true);

    try {
      const originUrl = window.location.origin;
      const response = await axios.post(`${API}/donations/checkout`, {
        package_id: isCustom ? 'custom' : selectedPackage,
        origin_url: originUrl,
        custom_amount: isCustom ? parseFloat(customAmount) : null,
        donor_name: donorName || null,
        donor_email: donorEmail || null
      });

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error('Donation error:', error);
      toast.error('Failed to process donation. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const getSelectedAmount = () => {
    if (isCustom) return parseFloat(customAmount) || 0;
    const pkg = donationOptions.find(p => p.id === selectedPackage);
    return pkg?.amount || 0;
  };

  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-[#FDFBF7]" data-testid="donate-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-[#FF7A59] rounded-full mb-6">
              <Heart className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-[#1E293B] font-['Fredoka'] mb-6">
              Make a <span className="text-[#FACC15]">Difference</span>
            </h1>
            <p className="text-xl text-[#64748B] leading-relaxed">
              Your donation helps us provide education, mentorship, and joy to children in Kenya. 
              Every contribution, no matter the size, creates a lasting impact.
            </p>
          </div>
        </div>
      </section>

      {/* Donation Form */}
      <section className="py-16 bg-white" data-testid="donation-form">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          {/* Impact Metrics */}
          <div className="bg-[#FDFBF7] rounded-3xl p-8 mb-12">
            <h2 className="text-2xl font-bold text-[#1E293B] font-['Fredoka'] mb-6 text-center">
              What Your Donation Does
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
              <div>
                <div className="text-3xl font-bold text-[#FACC15]">$25</div>
                <p className="text-sm text-[#64748B]">School supplies for 1 child</p>
              </div>
              <div>
                <div className="text-3xl font-bold text-[#38BDF8]">$50</div>
                <p className="text-sm text-[#64748B]">Feed 10 children</p>
              </div>
              <div>
                <div className="text-3xl font-bold text-[#FF7A59]">$100</div>
                <p className="text-sm text-[#64748B]">Sponsor a competition</p>
              </div>
              <div>
                <div className="text-3xl font-bold text-[#22C55E]">$500</div>
                <p className="text-sm text-[#64748B]">Full event sponsor</p>
              </div>
            </div>
          </div>

          {/* Package Selection */}
          <div className="mb-10">
            <h3 className="text-xl font-bold text-[#1E293B] font-['Fredoka'] mb-6">
              Choose Your Donation
            </h3>
            <div className="grid md:grid-cols-2 gap-4">
              {donationOptions.map((option) => (
                <button
                  key={option.id}
                  onClick={() => handleSelectPackage(option.id)}
                  data-testid={`donation-${option.id}`}
                  className={`p-6 rounded-2xl border-2 text-left transition-all duration-200 ${
                    selectedPackage === option.id
                      ? 'border-[#FACC15] bg-[#FACC15]/5 ring-2 ring-[#FACC15]'
                      : 'border-[#E2E8F0] hover:border-[#FACC15]/50'
                  }`}
                >
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 ${option.color} rounded-xl flex items-center justify-center flex-shrink-0`}>
                      <option.icon className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <h4 className="font-bold text-[#1E293B]">{option.name}</h4>
                        {selectedPackage === option.id && (
                          <Check className="w-5 h-5 text-[#FACC15]" />
                        )}
                      </div>
                      <p className="text-2xl font-bold text-[#1E293B] mt-1">${option.amount}</p>
                      <p className="text-sm text-[#64748B] mt-1">{option.description}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>

            {/* Custom Amount */}
            <button
              onClick={handleCustomSelect}
              data-testid="donation-custom"
              className={`w-full mt-4 p-6 rounded-2xl border-2 text-left transition-all duration-200 ${
                isCustom
                  ? 'border-[#FACC15] bg-[#FACC15]/5 ring-2 ring-[#FACC15]'
                  : 'border-[#E2E8F0] hover:border-[#FACC15]/50'
              }`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-bold text-[#1E293B]">Custom Amount</h4>
                  <p className="text-sm text-[#64748B]">Choose your own donation amount</p>
                </div>
                {isCustom && <Check className="w-5 h-5 text-[#FACC15]" />}
              </div>
            </button>

            {isCustom && (
              <div className="mt-4">
                <Label htmlFor="custom-amount" className="text-[#1E293B] font-semibold">
                  Enter Amount (USD)
                </Label>
                <div className="relative mt-2">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#64748B] font-bold">$</span>
                  <Input
                    id="custom-amount"
                    type="number"
                    min="1"
                    step="0.01"
                    value={customAmount}
                    onChange={(e) => setCustomAmount(e.target.value)}
                    data-testid="custom-amount-input"
                    className="pl-8 rounded-xl border-[#E2E8F0] focus:ring-2 focus:ring-[#FACC15]/50 focus:border-[#FACC15]"
                    placeholder="0.00"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Donor Info (Optional) */}
          <div className="mb-10">
            <h3 className="text-xl font-bold text-[#1E293B] font-['Fredoka'] mb-6">
              Your Information (Optional)
            </h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="donor-name" className="text-[#1E293B] font-semibold">
                  Name
                </Label>
                <Input
                  id="donor-name"
                  type="text"
                  value={donorName}
                  onChange={(e) => setDonorName(e.target.value)}
                  data-testid="donor-name-input"
                  className="mt-2 rounded-xl border-[#E2E8F0] focus:ring-2 focus:ring-[#FACC15]/50 focus:border-[#FACC15]"
                  placeholder="Your name"
                />
              </div>
              <div>
                <Label htmlFor="donor-email" className="text-[#1E293B] font-semibold">
                  Email
                </Label>
                <Input
                  id="donor-email"
                  type="email"
                  value={donorEmail}
                  onChange={(e) => setDonorEmail(e.target.value)}
                  data-testid="donor-email-input"
                  className="mt-2 rounded-xl border-[#E2E8F0] focus:ring-2 focus:ring-[#FACC15]/50 focus:border-[#FACC15]"
                  placeholder="your@email.com"
                />
              </div>
            </div>
          </div>

          {/* Donate Button */}
          <div className="text-center">
            <button
              onClick={handleDonate}
              disabled={isLoading || (!selectedPackage && !isCustom) || (isCustom && !customAmount)}
              data-testid="submit-donation-btn"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-[#FACC15] text-[#422006] font-bold px-12 py-4 text-lg shadow-[0_4px_14px_0_rgba(250,204,21,0.39)] hover:shadow-[0_6px_20px_rgba(250,204,21,0.23)] hover:-translate-y-1 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <Heart className="w-5 h-5" />
                  Donate ${getSelectedAmount().toFixed(2)}
                </>
              )}
            </button>
            <p className="text-sm text-[#64748B] mt-4">
              Secure payment powered by Stripe
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DonatePage;
